<template>
    <div
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <div>
            <div class="text-h4 text-weight-medium">
                {{ $t('Facility Details') }}
            </div>
            <div class="page-description">
                {{
                    $t('General information about {facilityName}.', {
                        facilityName: facility?.name
                    })
                }}
            </div>
        </div>
        <q-form @submit="update">
            <q-input
                v-model="form.name"
                @clear="form.name = ''"
                :label="$t('Name')"
                :rules="[v => v !== '' || $t('Enter a name')]"
                class="q-mb-sm q-pb-md"
                style="width: 100%"
                clearable
                lazy-rules
                outlined
                bg-color="white"
                color="accent"
            >
            </q-input>

            <q-input
                v-model="form.description"
                @clear="form.description = ''"
                :label="$t('Description')"
                class="q-mb-sm q-pb-md"
                style="width: 100%"
                outlined
                lazy-rules
                clearable
                bg-color="white"
                color="accent"
            >
            </q-input>

            <q-input
                v-model="form.address1"
                @clear="form.address1 = ''"
                :label="`${$t('Address')}`"
                :rules="[v => v !== '' || $t('Enter an address')]"
                class="q-mb-sm q-pb-md"
                style="width: 100%"
                clearable
                lazy-rules
                outlined
                bg-color="white"
                color="accent"
            >
            </q-input>

            <q-input
                v-model="form.address2"
                @clear="form.address2 = ''"
                :label="`${$t('Apt, Suite, Bldg, Code (Optional)')}`"
                class="q-mb-sm q-pb-md"
                style="width: 100%"
                clearable
                lazy-rules
                outlined
                bg-color="white"
                color="accent"
            >
            </q-input>

            <q-input
                v-model="form.city"
                @clear="form.city = ''"
                :label="`${$t('City')}`"
                :rules="[v => v !== '' || $t('Enter a city name')]"
                class="q-mb-sm q-pb-md"
                style="width: 100%"
                clearable
                lazy-rules
                outlined
                bg-color="white"
                color="accent"
            >
            </q-input>

            <q-select
                ref="countrySelect"
                v-model="form.country"
                @filter="handleFilter"
                :label="`${$t('Country')}`"
                :options="Object.values(countryOptions)"
                class="q-mb-sm q-pb-md"
                color="accent"
                style="width: 100%"
                input-debounce="0"
                lazy-rules
                outlined
                use-input
                bg-color="white"
            />

            <q-select
                ref="timezoneSelect"
                v-model="form.timezone"
                @filter="handleFilter"
                :label="`${$t('Timezone')}`"
                :options="timezoneOptions"
                class="q-mb-sm q-pb-md"
                color="accent"
                style="width: 100%"
                input-debounce="0"
                lazy-rules
                outlined
                use-input
                bg-color="white"
            />

            <q-select
                v-if="this.$isUserAdmin() && unitSystem"
                v-model="form.unit_system"
                :label="`${$t('Unit System')}`"
                :options="unitSystemOptions"
                class="q-mb-sm q-pb-md text-capitalize"
                color="accent"
                style="width: 100%"
                input-debounce="0"
                lazy-rules
                outlined
                emit-value
                bg-color="white"
            />

            <br />

            <div class="form__actions">
                <q-btn
                    :label="$t('Save')"
                    :disable="$userCannot('edit', 'facilities')"
                    class="float-right"
                    type="submit"
                    color="primary"
                />
                <q-btn
                    :label="$t('Cancel')"
                    @click="$router.go(-1)"
                    class="float-right q-mr-sm bg-white"
                    text-color="primary"
                    outline
                />
            </div>
        </q-form>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { ISO_3166_COUNTRY_CODES, getKeyByLabel } from '@models/countries.js';
import { TIMEZONES } from '@models/timezones.js';
import { mapState, mapActions } from 'pinia';
import { useFacilitiesStore } from '@stores/facilities';

export default {
    name: 'FacilityDetail',
    mixins: [BreadcrumbsManager],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    mounted() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Facility Details')
            }
        ]);

        // initialize form
        this.initForm();
    },
    data() {
        return {
            // models
            getKeyByLabel: getKeyByLabel,
            countryOptions: ISO_3166_COUNTRY_CODES,
            timezoneOptions: TIMEZONES,
            unitSystemOptions: [
                {
                    // Translators: Unit system for metric system
                    label: this.$t('Metric'),
                    value: 'metric'
                },
                {
                    // Translators: Unit system for imperial system
                    label: this.$t('Imperial'),
                    value: 'imperial'
                }
            ],

            form: {
                name: '',
                description: '',
                address1: '',
                address2: '',
                zip_code: '',
                city: '',
                country: 'Canada',
                timezone: 'UTC',
                unit_system: 'metric'
            }
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, {
            getFacility: 'getFacility'
        }),
        /**
         * Get facility details.
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Get unit system.
         */
        unitSystem() {
            return this.facility?.unit_system;
        },
        /**
         * Server formatted payload.
         * @return {Object} - Server formatted payload of the form.
         */
        payload() {
            // copy form
            const payload = Object.assign({}, this.form);

            // use country key instead of label
            payload['country'] = this.getKeyByLabel(this.form['country']);

            // Backwards Compatibility: if the facility's unit system does not exist,
            // it is using an older API version. Hence, exclude it from the request payload.
            if (!this.unitSystem) {
                delete payload['unit_system'];
            }

            return payload;
        }
    },
    methods: {
        ...mapActions(useFacilitiesStore, ['updateFacilityAction']),
        /**
         * Initialize form.
         */
        initForm() {
            this.form = Object.assign(this.form, this.facility);

            // use country label instead of code
            this.form['country'] = ISO_3166_COUNTRY_CODES[this.form['country']];
        },
        /**
         * Handle filtering for autocompletion in select dropdowns.
         */
        handleFilter(val, update) {
            if (val === '') {
                // show all options
                update(() => {
                    // get the q-select component that is active
                    if (this.$refs['countrySelect']['focused']) {
                        this.countryOptions = ISO_3166_COUNTRY_CODES;
                    }
                    if (this.$refs['timezoneSelect']['focused']) {
                        this.timezoneOptions = TIMEZONES;
                    }
                });
            } else {
                // update options
                update(() => {
                    let filterVal = val.toLowerCase();
                    // get the q-select component that is active
                    if (this.$refs['countrySelect']['focused']) {
                        const countryLabels = Object.values(
                            ISO_3166_COUNTRY_CODES
                        );
                        this.countryOptions = countryLabels.filter(label => {
                            return label.toLowerCase().includes(filterVal);
                        });
                    }
                    if (this.$refs['timezoneSelect']['focused']) {
                        this.timezoneOptions = TIMEZONES.filter(label => {
                            return label.toLowerCase().includes(filterVal);
                        });
                    }
                });
            }
        },
        /**
         * Update facility.
         */
        async update() {
            // dispatch action
            await this.updateFacilityAction(this.payload)
                .then(response => {
                    this.$notifySuccess(this.$t('Facility updated'));
                    this.$router.go(-1);
                })
                .catch(e => {
                    const error =
                        e?.response?.data && typeof e.response.data === 'object'
                            ? e.response.data
                            : {};
                    if (
                        'name' in error &&
                        error['name'].length > 0 &&
                        error['name'][0] ==
                            'facility with this name already exists.'
                    ) {
                        this.$notifyError(
                            this.$t(
                                'Facility with this name already exists. Please use a different name.'
                            ),
                            error
                        );
                    } else {
                        this.$notifyError(
                            'Failed to update facility. Please try again later.',
                            error
                        );
                    }
                });
        }
    }
};
</script>

<style lang="scss" scoped></style>
